/**
 * BackToDirectory: the back control for the pages that hang off the
 * Directory (organization and person detail).
 *
 * It returns to the Directory search this tab last left (see
 * src/lib/directoryReturn.ts): query, view, filters, sort and paging
 * depth are all in that URL, so the reader lands on the same results.
 * It does NOT walk history: a reloaded or deep-linked detail page has no
 * directory entry behind it, and an organization reached through a
 * person has the person behind it. Cold entry falls back to the
 * view-appropriate Directory (people for a person page, organizations
 * for an organization page).
 */
import type { ReactNode } from 'react';
import { BackControl } from '@/components/BackControl';
import { readDirectorySearch } from '@/lib/directoryReturn';

export interface BackToDirectoryProps {
  tenantSlug: string;
  fallbackView: 'people' | 'organizations';
}

export function BackToDirectory({ tenantSlug, fallbackView }: BackToDirectoryProps): ReactNode {
  return (
    <BackControl
      label="Directory"
      ariaLabel="Back to directory"
      to={`/t/${encodeURIComponent(tenantSlug)}/directory?view=${fallbackView}`}
      remembered={readDirectorySearch}
      walkHistory={false}
      testId="back-to-directory"
      className="mb-4"
    />
  );
}
